import React, { useState } from 'react'
import type { MonthlyChartPoint } from '../../../utils/projectDetailChartData'
import type { ProjectDetailStats } from '../../../types/projectAnalysis'
import { MONTH_NAMES } from '../../../utils/projectAnalysisPeriod'
import { ExportMenuButton } from '../../common/export-menu-button'
import {
  exportMonthlyCsv,
  exportMonthlyExcel,
  getMonthlyExportRows,
  safeFilename,
} from '../../../utils/projectAnalysisExport'
import { utilBadgeClass } from './project-detail-util-badge'
import { ProjectDetailMonthlyHoursTrendChart } from './project-detail-monthly-hours-trend-chart'

export interface ProjectDetailMonthlyTabProps {
  stats: ProjectDetailStats
  projectDetailName: string
  isDark: boolean
  monthlyChartData: MonthlyChartPoint[]
}

export function ProjectDetailMonthlyTab({
  stats: s,
  projectDetailName,
  isDark,
  monthlyChartData,
}: ProjectDetailMonthlyTabProps) {
  const nameSafe = safeFilename(projectDetailName)
  const [view, setView] = useState<'chart' | 'table'>('table')
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(
    () => new Set(),
  )

  const toggleMonth = (ymKey: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev)
      if (next.has(ymKey)) next.delete(ymKey)
      else next.add(ymKey)
      return next
    })
  }

  const resourceLabel = (r: {
    full_name?: string
    username?: string
    user_id: number
  }) => r.full_name || r.username || `User ${r.user_id}`

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100 gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-0.5 h-4 rounded-full bg-blue-500 flex-none self-start mt-0.5" />
          <div className="min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600 block">
              Monthly breakdown
            </span>
            {view === 'table' ? (
              <span className="text-[10px] text-slate-400 mt-0.5 block">
                Click a month to expand per-resource hours (same rollup as
                Resources → monthly).
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end w-full sm:w-auto">
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
            {(['chart', 'table'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors capitalize ${
                  view === v
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500'
                }`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <ExportMenuButton
            disabled={!s.monthlyData?.length}
            onCsv={() => {
              const rows = getMonthlyExportRows(s)
              exportMonthlyCsv(rows, nameSafe)
            }}
            onExcel={() => {
              const rows = getMonthlyExportRows(s)
              exportMonthlyExcel(rows, nameSafe)
            }}
          />
        </div>
      </div>

      {view === 'table' ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-8 px-2 py-2" aria-hidden />
                <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase text-slate-500">
                  Period
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                  Total
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                  Billable
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                  Non-bill.
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                  Util.
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                  Late %
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                  Leave
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                  Resources
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {s.monthlyData.map((m) => {
                const util =
                  m.total_hours > 0
                    ? (m.billable_hours / m.total_hours) * 100
                    : 0
                const entries = Number(m.entries_count) || 0
                const late = Number(m.late_entries_count) || 0
                const latePct = entries > 0 ? (late / entries) * 100 : null
                const leaveDays = Math.round((m.leave_days ?? 0) * 10) / 10
                const resourceRows =
                  s.monthlyResourceBreakdown.get(m.name) ?? []
                const canExpand = resourceRows.length > 0
                const isExpanded = expandedMonths.has(m.name)
                return (
                  <React.Fragment key={m.name}>
                    <tr
                      onClick={() => canExpand && toggleMonth(m.name)}
                      className={
                        canExpand
                          ? [
                              'cursor-pointer',
                              isExpanded ? 'bg-slate-100' : '',
                              'hover:bg-slate-50',
                            ].join(' ')
                          : 'hover:bg-slate-50'
                      }
                    >
                      <td className="px-2 py-2 text-center">
                        {canExpand ? (
                          <span
                            className="inline-block transition-transform text-slate-500"
                            style={{
                              transform: isExpanded
                                ? 'rotate(90deg)'
                                : 'none',
                            }}
                            aria-hidden
                          >
                            ▸
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-slate-900">
                        {MONTH_NAMES[m.month - 1]} {m.year}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                        {m.total_hours.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-emerald-700 font-medium tabular-nums">
                        {m.billable_hours.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                        {m.non_billable_hours.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${utilBadgeClass(util)}`}
                        >
                          {util.toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-slate-600">
                        {latePct != null ? `${latePct.toFixed(1)}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                        {leaveDays.toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                        {m.unique_users}
                      </td>
                    </tr>
                    {isExpanded && canExpand && (
                      <tr className="bg-slate-50/80">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="rounded-lg border border-slate-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-slate-200">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="px-3 py-1.5 text-left text-[10px] font-medium uppercase text-slate-500">
                                    Resource
                                  </th>
                                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                    Total
                                  </th>
                                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                    Billable
                                  </th>
                                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                    Non-bill.
                                  </th>
                                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                    Util.
                                  </th>
                                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                    Late %
                                  </th>
                                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                    Leave
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {resourceRows.map((r) => {
                                  const rowEntries = Number(r.entries_count) || 0
                                  const rowLate =
                                    Number(r.late_entries_count) || 0
                                  const rowLatePct =
                                    rowEntries > 0
                                      ? (rowLate / rowEntries) * 100
                                      : null
                                  const rowLeave =
                                    Math.round((r.leave_days ?? 0) * 10) / 10
                                  return (
                                    <tr key={r.user_id}>
                                      <td className="px-3 py-1.5 text-[11px] font-medium text-slate-700">
                                        {resourceLabel(r)}
                                      </td>
                                      <td className="px-3 py-1.5 text-right text-[11px] text-slate-600 tabular-nums">
                                        {r.total_hours.toLocaleString(
                                          undefined,
                                          { maximumFractionDigits: 2 },
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5 text-right text-[11px] text-emerald-700 tabular-nums">
                                        {r.billable_hours.toLocaleString(
                                          undefined,
                                          { maximumFractionDigits: 2 },
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5 text-right text-[11px] text-slate-600 tabular-nums">
                                        {r.non_billable_hours.toLocaleString(
                                          undefined,
                                          { maximumFractionDigits: 2 },
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5 text-right">
                                        <span
                                          className={`px-1 py-0.5 rounded text-[10px] font-medium ${utilBadgeClass(r.util)}`}
                                        >
                                          {r.util.toFixed(0)}%
                                        </span>
                                      </td>
                                      <td className="px-3 py-1.5 text-right text-[11px] text-slate-600">
                                        {rowLatePct != null
                                          ? `${rowLatePct.toFixed(1)}%`
                                          : '—'}
                                      </td>
                                      <td className="px-3 py-1.5 text-right text-[11px] text-slate-600 tabular-nums">
                                        {rowLeave.toLocaleString(undefined, {
                                          maximumFractionDigits: 1,
                                        })}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4 min-h-[320px]">
          {monthlyChartData.length > 0 ? (
            <ProjectDetailMonthlyHoursTrendChart
              isDark={isDark}
              monthlyChartData={monthlyChartData}
            />
          ) : (
            <p className="text-sm text-slate-500 text-center py-16">
              No monthly rows for this period.
            </p>
          )}
        </div>
      )}
    </section>
  )
}
